import { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";

import {
  useBulkRegisterMutation,
  useBulkUpdateMutation,
  useGetBulkRecordStatusQuery,
  useGetOfflineUploadStatusQuery,
  useBulkEventRescheduleMutation,
  useBulkEventDeleteMutation,
  useBulkUploadOfflineResponsesMutation,
  useGetBulkJobLogsQuery,
  useGetJobRecordsQuery,
  useBulkRegisterGlobalEventsMutation,
} from "./uploadMutation";
import { useSelector } from "react-redux";
import { getUserLoginId } from "../auth/selectors";

const useUploadsQueries = () => {
  const location = useLocation();
  const isBulkUploadJobsActive = location.pathname === "/bulk-upload-jobs";
  const hasInitializedRef = useRef(false);
  const wasInitializingRef = useRef(false);

  // Mark as initialized immediately when page becomes active for the first time
  if (isBulkUploadJobsActive && !hasInitializedRef.current) {
    hasInitializedRef.current = true;
    wasInitializingRef.current = true;
  } else if (isBulkUploadJobsActive) {
    wasInitializingRef.current = false;
  }

  const [bulkRegister] = useBulkRegisterMutation();
  const [bulkUpdate] = useBulkUpdateMutation();
  const [bulkEventReschedule] = useBulkEventRescheduleMutation();
  const [bulkEventDelete] = useBulkEventDeleteMutation();
  const [bulkUploadOfflineResponses] = useBulkUploadOfflineResponsesMutation();
  const [bulkRegisterGlobalEvents] = useBulkRegisterGlobalEventsMutation();
  const userLoginId = useSelector(getUserLoginId);
  const [currentJobId, setCurrentJobId] = useState(null);
  const [currentUploadType, setCurrentUploadType] = useState(null);

  const useBulkJobLogs = (payload) => {
    const skipQuery =
      !hasInitializedRef.current ||
      payload.status.length === 0 ||
      payload.job_type.length === 0;

    const { data, isLoading, refetch, isFetching } = useGetBulkJobLogsQuery(
      payload,
      {
        skip: skipQuery,
        // Only refetch on mount if this is the first initialization
        refetchOnMountOrArgChange: wasInitializingRef.current,
      },
    );
    return { data, isLoading, refetch, isFetching };
  };

  const useJobRecords = (jobId, jobType, status) => {
    // Omitting status, not required anymore
    status = "";

    const { data, isLoading, refetch } = useGetJobRecordsQuery(
      { jobId, jobType, status },
      {
        skip: !jobId || !jobType,
      },
    );
    return { data, isLoading, refetch };
  };

  const { data: bulkStatusData, refetch: refetchBulkStatus } =
    useGetBulkRecordStatusQuery(currentJobId, {
      skip: !currentJobId || currentUploadType === "uploadOfflineResponses",
    });

  const { data: offlineStatusData, refetch: refetchOfflineStatus } =
    useGetOfflineUploadStatusQuery(currentJobId, {
      skip: !currentJobId || currentUploadType !== "uploadOfflineResponses",
    });

  const handleBulkRegister = async (schoolId, formData) => {
    try {
      const response = await bulkRegister({
        schoolId,
        formData,
      }).unwrap();

      return {
        id: response?._id,
        jobId: response?.job_id,
      };
    } catch (error) {
      console.error("Error in bulk register", error);
      throw error;
    }
  };

  const handleBulkUpdate = async (schoolId, formData) => {
    try {
      const response = await bulkUpdate({
        schoolId,
        formData,
      }).unwrap();
      return {
        id: response?._id,
        jobId: response?.job_id,
      };
    } catch (error) {
      console.error("Error in bulk update", error);
      throw error;
    }
  };

  const handleGetBulkRecordStatus = (jobId, uploadType) => {
    setCurrentUploadType(uploadType);
    setCurrentJobId(jobId);
    return {
      statusData:
        uploadType === "uploadOfflineResponses"
          ? offlineStatusData
          : bulkStatusData,
      refetchStatus: async () => {
        try {
          const result =
            uploadType === "uploadOfflineResponses"
              ? await refetchOfflineStatus()
              : await refetchBulkStatus();
          return result.data;
        } catch (error) {
          console.error("Error refetching status", error);
          return null;
        }
      },
    };
  };

  const handleBulkEventCreate = async (schoolId, formData) => {
    try {
      console.error("Bulk Event Create:", {
        schoolId,
        formData: Object.fromEntries(formData.entries()),
      });
      // Mock successful response
      return {
        id: "mock-event-create-id",
        jobId: "mock-event-create-job",
      };
    } catch (error) {
      console.error("Error in bulk event create", error);
      throw error;
    }
  };

  const handleBulkEventReschedule = async (schoolId, formData) => {
    try {
      const response = await bulkEventReschedule({
        schoolId,
        uploadedBy: userLoginId,
        formData,
      }).unwrap();
      return {
        id: response?._id,
        jobId: response?.job_id,
      };
    } catch (error) {
      console.error("Error in bulk event reschedule", error);
      throw error;
    }
  };

  const handleBulkEventDelete = async (schoolId, formData) => {
    try {
      // Rename formData's fileobject to events_file
      const fileObject = formData.get("fileobject");
      formData.delete("fileobject");
      formData.append("events_file", fileObject);
      const response = await bulkEventDelete({ formData }).unwrap();
      return {
        id: response?._id,
        jobId: response?.job_id,
      };
    } catch (error) {
      console.error("Error in bulk event delete", error);
      throw error;
    }
  };

  const handleBulkUploadOfflineResponses = async (formData) => {
    const mapOfflineErrors = (errors = []) =>
      errors.map(({ record = {}, error: failure_reason }) => ({
        ...record,
        failure_reason: failure_reason || "Validation failed",
      }));

    try {
      const response = await bulkUploadOfflineResponses({
        formData,
      }).unwrap();

      return {
        id: response?.filename,
        jobId: response?.job_id,
        status: response?.status,
        message: response?.message,
      };
    } catch (error) {
      console.error("Error in bulk upload offline responses", error);

      const detail = error?.data?.detail;
      const validationErrors = detail?.errors || undefined;

      if (validationErrors?.length) {
        return {
          status: "failed",
          detail: mapOfflineErrors(validationErrors),
          message: detail?.message || "Validation failed",
        };
      }
      throw error;
    }
  };

  const handleBulkGlobalRegister = async (formData) => {
    try {
      const response = await bulkRegisterGlobalEvents({
        formData,
      }).unwrap();
      return {
        id: response?._id,
        jobId: response?.job_id,
      };
    } catch (error) {
      console.error("Error in bulk global register", error);
      throw error;
    }
  };

  const clearStatusData = () => {
    setCurrentJobId(null);
    setCurrentUploadType(null);
  };

  return {
    handleBulkRegister,
    handleBulkUpdate,
    handleGetBulkRecordStatus,
    handleBulkEventCreate,
    handleBulkEventReschedule,
    handleBulkEventDelete,
    handleBulkUploadOfflineResponses,
    handleBulkGlobalRegister,
    useBulkJobLogs,
    useJobRecords,
    clearStatusData,
  };
};

export default useUploadsQueries;
